import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Rocket, Layers, ShoppingCart, IndianRupee, Leaf, TrendingUp, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { Panel, AnimatedNumber } from "@/components/common/Widgets";
import { fmtNum, fmtLakh, fmtINR } from "@/lib/format";

const OPTIMIZERS = [
  { icon: Layers, name: "Supplier Blend Optimizer", desc: "Auto-derives the optimal multi-supplier coal blend for quality & cost targets." },
  { icon: ShoppingCart, name: "Coal Purchase Optimizer", desc: "Times purchases against price, freight & inventory to minimise landed cost." },
  { icon: TrendingUp, name: "Production Mix Optimizer", desc: "Shifts MEG/DEG/TEG mix toward the highest contribution margin." },
  { icon: IndianRupee, name: "Cost Minimization Engine", desc: "Minimises ₹/tonne product across the full coal→product chain." },
  { icon: Leaf, name: "Carbon Minimization Engine", desc: "Finds the lowest-carbon operating point at equal throughput." },
  { icon: Rocket, name: "Profitability Optimizer", desc: "Jointly optimises cost, carbon & output for maximum EBITDA." },
];

export default function Phase2() {
  const [plan, setPlan] = useState({ meg: 820, deg: 280, teg: 145 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try { setResult(await api.procurement({ meg: Number(plan.meg), deg: Number(plan.deg), teg: Number(plan.teg) })); }
    finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-4 animate-fade-up" data-testid="phase2">
      <div className="flex items-center gap-3">
        <div>
          <div className="eyebrow" style={{ color: "#fab20b" }}>Future Intelligence · Phase 2 Preview</div>
          <h1 className="font-display font-extrabold text-2xl uppercase tracking-tight text-ink">From EMS to Procurement Decision Engine</h1>
        </div>
        <span className="ml-auto text-[0.62rem] font-mono px-2.5 py-1 border border-brand-gold/40 text-brand-gold uppercase tracking-wider">Unlocks after 6 months of learning</span>
      </div>

      <Panel eyebrow="Phase 2 · Available as Preview" title="Coal Procurement Advisor" testid="procurement-advisor"
        right={<ShoppingCart size={16} className="text-brand-gold" />}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div>
            <div className="text-[0.78rem] text-ink-soft mb-3">Enter next month's daily production plan:</div>
            <div className="space-y-2">
              {["meg", "deg", "teg"].map((k) => (
                <div key={k} className="flex items-center gap-2">
                  <span className="eyebrow w-12">{k.toUpperCase()}</span>
                  <input type="number" value={plan[k]} data-testid={`proc-input-${k}`}
                    onChange={(e) => setPlan({ ...plan, [k]: e.target.value })}
                    className="flex-1 panel-2 px-3 py-2 text-ink font-mono text-sm focus:outline-none focus:border-brand-gold" />
                  <span className="text-[0.66rem] text-ink-soft font-mono">MT/day</span>
                </div>
              ))}
            </div>
            <button onClick={run} disabled={loading} data-testid="proc-run"
              className="w-full mt-3 py-2.5 font-display font-bold uppercase tracking-wider text-sm transition-colors"
              style={{ background: "rgba(250,178,11,0.16)", color: "#fab20b", border: "1px solid #fab20b55" }}>
              {loading ? "Optimising…" : "Generate Procurement Plan"}
            </button>
          </div>

          {result && (
            <div className="lg:col-span-2 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <Big label="Required Coal" value={result.required_coal_mt} unit="MT/mo" color="#FFFFFF" />
                <BigText label="Expected Saving" value={fmtLakh(result.expected_saving)} color="#33b76d" />
                <BigText label="Carbon Reduction" value={`${fmtNum(result.expected_carbon_reduction_pct, 1)}%`} color="#33b76d" />
              </div>
              <div className="panel-2 px-3 py-3">
                <div className="eyebrow mb-2">Recommended Purchases</div>
                <div className="space-y-2">
                  {result.purchases.map((p) => (
                    <div key={p.id} className="flex items-center gap-2">
                      <span className="text-[0.76rem] text-ink w-24">{p.name}</span>
                      <div className="flex-1 h-2.5 bg-[#16223d]"><div className="h-full bg-brand-gold" style={{ width: `${p.share}%` }} /></div>
                      <span className="text-[0.72rem] font-mono tnum text-ink w-24 text-right">{fmtNum(p.quantity_mt)} MT</span>
                      <span className="text-[0.62rem] font-mono px-1.5 text-ink-soft w-20 text-right">{p.classification}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-[0.68rem] font-mono text-ink-soft">This is no longer an EMS — it's a procurement decision engine. Figures are illustrative Phase-2 projections.</div>
            </div>
          )}
        </div>
      </Panel>

      <div>
        <div className="eyebrow mb-3">Advanced Optimizers · Coming in Phase 2</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="optimizer-preview">
          {OPTIMIZERS.map((o, i) => (
            <motion.div key={o.name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="panel relative overflow-hidden p-4" data-testid={`optimizer-${i}`} style={{ background: "rgba(10,18,36,0.6)" }}>
              <div className="absolute top-3 right-3 text-ink-soft/40"><Lock size={13} /></div>
              <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ background: "rgba(250,178,11,0.12)", color: "#fab20b" }}>
                <o.icon size={18} />
              </div>
              <div className="font-display font-bold text-sm uppercase tracking-tight text-ink">{o.name}</div>
              <div className="text-[0.74rem] text-ink-soft mt-1.5 leading-relaxed">{o.desc}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Big({ label, value, unit, color }) {
  return (
    <div className="panel-2 px-3 py-3 text-center">
      <div className="eyebrow truncate">{label}</div>
      <div className="text-2xl font-bold tnum mt-1" style={{ color }}><AnimatedNumber value={value} /> </div>
      <div className="text-[0.6rem] text-ink-soft font-mono">{unit}</div>
    </div>
  );
}
function BigText({ label, value, color }) {
  return (
    <div className="panel-2 px-3 py-3 text-center">
      <div className="eyebrow truncate">{label}</div>
      <div className="text-2xl font-bold tnum mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
